/**
 * ч.1105: спільне підтвердження незворотної дії над організацією — назва
 * (вручну), пароль облікового запису і, якщо організацію підтверджено ключем
 * КЕП, — підпис запиту ключем САМЕ цієї організації.
 * Використовують «Видалити організацію», «Передача організації», «Видалити акаунт».
 */
import { useState } from 'react';
import KeyAuthPanel from '@/shared/eusign/KeyAuthPanel';
import { signKepAction } from '@/shared/eusign/kepAction';
import { accountOrgs, type DestructivePayload, type KepAction } from '@/api/accountOrgs';

interface Props {
  action: KepAction;
  org: { id: string; name: string; code: string | null; tenant_id?: string };
  kepRequired: boolean;
  /** Текст кнопки (малою літерою, без іконок). */
  submitLabel: string;
  busyLabel: string;
  disabled?: boolean;
  disabledTitle?: string;
  onSubmit: (payload: DestructivePayload) => Promise<void>;
}

const sameName = (a: string, b: string) =>
  a.trim().replace(/\s+/g, ' ').toLocaleLowerCase('uk') === b.trim().replace(/\s+/g, ' ').toLocaleLowerCase('uk');

export default function DestructiveConfirm({
  action, org, kepRequired, submitLabel, busyLabel, disabled, disabledTitle, onSubmit,
}: Props) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameOk = sameName(name, org.name);
  const lock = disabled ? disabledTitle
    : !nameOk ? 'Введіть назву організації точно, як у списку'
      : !password ? 'Введіть пароль облікового запису' : undefined;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      let kep: { challenge: string; signature_b64: string } | undefined;
      // ч.1406: організація іншого власного акаунта — запит на підпис від її акаунта.
      if (kepRequired) {
        kep = org.tenant_id
          ? await signKepAction(action, org.id, org.code ?? '', org.name,
            () => accountOrgs.kepChallenge(action, org.id, org.tenant_id))
          : await signKepAction(action, org.id, org.code ?? '', org.name);
      }
      await onSubmit({ password, confirm_name: name, ...kep });
      setPassword('');
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (e as Error)?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="destructive-confirm">
      {kepRequired && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem', marginBottom: '0.8rem' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Авторизація ключем КЕП організації</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Організацію «{org.name}» підтверджено ключем КЕП, тому дію треба підписати ключем саме цієї
            організації{org.code ? ` (код ${org.code})` : ''}. Ключ читається у браузері; пароль ключа на
            сервер не передається.
          </div>
          <KeyAuthPanel />
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 280, flex: '1 1 280px' }}>
          <label>Назва організації — введіть вручну: «{org.name}»</label>
          <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Назва організації"
            autoComplete="off" />
        </div>
        <div className="field" style={{ width: 220 }}>
          <label>Пароль облікового запису</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            aria-label="Пароль облікового запису" autoComplete="current-password" />
        </div>
      </div>
      {error && <div style={{ color: 'var(--badge-red-fg)', marginTop: '0.5rem' }} role="alert">{error}</div>}
      <button type="button" className="danger" style={{ marginTop: '0.8rem' }}
        disabled={!!lock || busy} title={lock} onClick={() => void submit()}>
        {busy ? busyLabel : submitLabel}
      </button>
    </div>
  );
}
